import * as Sentry from '@sentry/vue'
import * as stripe from '@stripe/stripe-js'
import { AxiosResponse } from 'axios'
import { cloneDeep } from 'lodash-es'
import { defineStore } from 'pinia'

import * as api from '@/api/api'
import { usePostHog } from '@/common/composables/usePostHog'
import {
  StorePerformance,
  StorePerformanceSeatingTime,
  StorePromotionCode,
  parsePerformance,
} from '@/models'
import CSDateService from '@/modules/csDate/services/CSDateService'
import GuestInviteCodeService from '@/modules/guestUi/services/GuestInviteCodeService'
import ReservationService from '@/modules/reservation/services/ReservationService'
import {
  EnrichedAddOn,
  EnrichedTable,
  OrderPricing,
  getPricing,
  priceAddOns,
  tablesForSeatedParty,
  toReservationParams,
} from '@/orders'
import { useSettingStore } from '@/stores/setting'
import { StoreUser, useUserStore } from '@/stores/user'
import { ErrorData, clearError, enhanceStripeError, mkHandledError } from '@/utils/errors'

import {
  CSDateRead,
  FeatureFlag,
  PerformanceAddOnRead,
  PerformanceListRead,
  PerformanceRead,
  PerformanceSeatingTimeId,
  PerformanceTableId,
  PromotionCodeRead,
  ReservationAddOnWrite,
  ReservationDetailedRead,
  ReservationGuestModify,
  ReservationGuestWrite,
  ResidencyId,
  ResidencyPublicRead,
} from '@generated/types'

export interface Checkout {
  reservation: ReservationDetailedRead | null
  reservation_key: string | null
  performance: StorePerformance | null
  tables: number[]
  seating_time: { id: PerformanceSeatingTimeId; time: string; datetime: string } | null
  number_of_guests: number
  add_ons: ReservationAddOnWrite[] | null
  contact: Contact
  invite_code: string | null
  promo_code: PromotionCodeRead | null
  hasPromoCodeApplied: boolean
  scroll_position_from_set_show: number | null
  client_secret: string | null
  is_desktop_flow: boolean
  checkout_success: boolean
  is_timer_expired: boolean
  unlockedPerformancesByInviteCode: PerformanceListRead[]
  gift_card: {
    number: string
    balance: number
  } | null
  csDates: CSDateRead[]
}

export interface Contact {
  email: string
  phone: string
  first_name: string
  last_name: string
  accepts_marketing: boolean
}

export const useCheckoutStore = defineStore('checkout', {
  persist: true,
  state(): Checkout {
    return {
      performance: null,
      number_of_guests: -1,
      seating_time: null,
      tables: [],
      add_ons: null,
      reservation: null,
      reservation_key: null,
      contact: {
        email: '',
        phone: '',
        first_name: '',
        last_name: '',
        accepts_marketing: true,
      } as Contact,
      invite_code: null,
      promo_code: null,
      hasPromoCodeApplied: false,
      scroll_position_from_set_show: null,
      client_secret: null,
      is_desktop_flow: false,
      checkout_success: false,
      is_timer_expired: false,
      unlockedPerformancesByInviteCode: [],
      gift_card: null,
      csDates: [],
    }
  },
  getters: {
    nextRoute(): { name: string } {
      if (this.isCheckoutReady) {
        return { name: 'setContact' }
      } else if (this.isAddOnsReady) {
        return { name: 'setAddOns' }
      } else if (this.isSetSeatingTimeReady) {
        return { name: 'setSeatingTime' }
      } else if (this.isSetTablesReady) {
        return { name: 'setTable' }
      } else {
        return { name: 'setGuests' }
      }
    },
    reservationGuestData(): ReservationGuestModify {
      if (!this.performance) return {}
      const result: ReservationGuestModify = {
        tables: this.tables,
        performance_id: this.performance.id,
        tables_details: this.tableModels.map(t => ({
          id: t.id,
          name: t.name,
          guests: t.guests,
          price_per_person: t.price_per_person,
        })),
        skip_guest: true,
        number_of_guests: this.number_of_guests,
        ...toReservationParams(this.pricing),
      }
      if (this.reservation_key) result.reservation_key = this.reservation_key
      if (this.seating_time) result.seating_time = this.seating_time.id
      if (this.invite_code) result.invite_code = this.invite_code
      if (this.promo_code) result.promo_code = this.promo_code.code
      if (this.add_ons) result.add_ons = this.add_ons
      return result
    },
    guestInfoData(): ReservationGuestWrite | null {
      if (this.hasSufficientGuestInfo) {
        return {
          ...this.contact,
          reservation_key: this.reservation_key,
        }
      } else {
        return null
      }
    },
    activeAddOns(): PerformanceAddOnRead[] {
      return (this.performance?.add_ons || []).filter(a => a.is_active)
    },
    hasAddOns(): boolean {
      return this.activeAddOns.length > 0
    },
    hasSelectedAddOns(): boolean {
      return this.add_ons !== null || !this.hasAddOns
    },
    hasSelectedSeatingTime(): boolean {
      return !!this.seating_time && this.seating_time?.id > 0
    },
    isSetGuestsReady(): boolean {
      return !!this.performance
    },
    isSetTablesReady(): boolean {
      return this.isSetGuestsReady && this.number_of_guests > 0
    },
    isSetSeatingTimeReady(): boolean {
      return this.isSetTablesReady && this.tables && this.tables.length > 0
    },
    isAddOnsReady(): boolean {
      return this.isSetSeatingTimeReady && this.hasSelectedSeatingTime && this.hasAddOns
    },
    isCheckoutReady(): boolean {
      return this.isSetSeatingTimeReady && this.hasSelectedSeatingTime && this.hasSelectedAddOns
    },
    isDetailsReady(): boolean {
      return this.isCheckoutReady && !!this.reservation?.id
    },
    hasSufficientGuestInfo(): boolean {
      return (
        /\S+@\S+\.\S+/.test(this.contact.email) &&
        this.contact.first_name.length > 1 &&
        this.contact.last_name.length > 1 &&
        this.contact.phone.length > 1
      )
    },
    requiresPayment(): boolean {
      return this.pricing.total > 0.5
    },
    tableModels(): EnrichedTable[] {
      return tablesForSeatedParty(this.performance, this.tables, this.number_of_guests)
    },
    addOnsModels(): EnrichedAddOn[] {
      if (!this.performance?.add_ons) return []
      return priceAddOns(this.performance.add_ons, this.add_ons)
    },
    percentOff(): number {
      return this.promo_code?.coupon?.percent_off || 0
    },
    // eslint-disable-next-line no-unused-vars
    discount(): (_: number) => number {
      // if this.percentOff is 0, then this becomes value * (1 - 0/100)
      return (value: number) => value * (1 - this.percentOff / 100)
    },
    pricing(): OrderPricing {
      return getPricing({
        tables: this.tableModels,
        addOns: this.addOnsModels,
        numberOfGuests: this.number_of_guests,
        serviceFeePerGuest: this.actualServiceFee,
        servicePercentage: this.actualServicePercentage,
        taxRate: useSettingStore().tax_rate,
        feeTaxRate: useSettingStore().fee_tax_rate,
        percentOff: this.percentOff,
        applicationFee: useSettingStore().application_fee,
        applicationFeePerReservation: useSettingStore().application_fee_per_reservation,
        giftCard: this.gift_card,
      })
    },
    scrollPositionFromSetShow(): number | null {
      return this.scroll_position_from_set_show
    },
    isDesktopFlow(): boolean {
      return this.is_desktop_flow
    },
    standingRoomOnly(): boolean {
      return this.performance?.tables?.length === 1 && this.performance?.tables[0].standing_room
    },
    actualServiceFee(): number {
      const settingStore = useSettingStore()
      if (!this.performance || this.performance.service_fee === null) {
        return settingStore.service_fee
      }
      return Number(this.performance.service_fee)
    },
    actualServicePercentage(): number {
      const settingStore = useSettingStore()
      if (!this.performance || this.performance.service_percentage === null) {
        return settingStore.service_percentage
      }
      return Number(this.performance.service_percentage)
    },
    stripeInstanceOptions() {
      return {
        stripeAccount: useSettingStore().stripe_account,
      }
    },
  },
  actions: {
    async getCSDates(start_date: string) {
      const { data } = await CSDateService.listCSDatesForArea({ start_date })
      this.csDates = cloneDeep(data)
      return this.csDates
    },
    clearAddOns() {
      this.add_ons = null
    },
    clearSeatingTime() {
      this.seating_time = null
    },
    clearTables() {
      this.tables = []
      this.clearSeatingTime()
    },
    clearNumberOfGuests() {
      this.number_of_guests = 0
      this.clearTables()
    },
    clearAll() {
      this.performance = null
      this.reservation = null
      this.reservation_key = null
      this.client_secret = null
      this.number_of_guests = -1
      this.tables = []
      this.seating_time = null
      this.add_ons = null
      this.promo_code = null
      this.hasPromoCodeApplied = false
      this.gift_card = null
      this.checkout_success = false
      this.invite_code = null
      this.unlockedPerformancesByInviteCode = []
    },
    async abandonAndClear() {
      try {
        // Why abandoning a reservation?
        // There’s a scenario where a user goes all the way back to the /setShow page but then changes their mind and wants tickets for the same table again.
        // Without the /abandon call, they’d have to wait until their previous reservation expires automatically.
        // By explicitly abandoning it, the table becomes instantly available again.
        await this.abandonAnonymousReservation()
        this.clearAll()
      } catch (e) {
        // If /abandon endpoint fails, it means that most likely such reservation was already abandoned
        // Still clear the checkout
        this.clearAll()
      }
    },

    async getPerformance(id: number): Promise<StorePerformance> {
      const response = await api.getPerformance(id)
      return parsePerformance(response.data)
    },

    getLastestPerformanceDatetime(
      performance: PerformanceRead,
      datetimePropsToCompare: (keyof Pick<
        PerformanceRead,
        'created' | 'publish_datetime' | 'on_sale_datetime' | 'off_sale_datetime' | 'datetime'
      >)[],
    ): string | null {
      const settingStore = useSettingStore()
      const nowMoment = settingStore.parseMoment(settingStore.nowDateTime())
      let latestDatetime = performance[datetimePropsToCompare[0]]
      for (const key of datetimePropsToCompare) {
        if (!performance[key]) continue
        if (
          performance[key] &&
          nowMoment.isBefore(settingStore.parseMoment(performance[key] as string))
        ) {
          latestDatetime = performance[key] as string
        }
      }
      return latestDatetime
    },

    isPerformanceVisibleOnTheList(performance: PerformanceRead): boolean {
      const settingStore = useSettingStore()
      const nowMoment = settingStore.parseMoment(settingStore.nowDateTime())
      return nowMoment.isBefore(
        this.getLastestPerformanceDatetime(performance, ['off_sale_datetime', 'datetime']),
      )
    },

    async getResidency(residencyId: ResidencyId | string): Promise<ResidencyPublicRead> {
      const response = await api.getResidency(residencyId)
      return response.data
    },

    async verifyInviteCode(code: string): Promise<PerformanceListRead[]> {
      const { data } = await GuestInviteCodeService.verify(code)
      this.unlockedPerformancesByInviteCode = cloneDeep(data)
      return this.unlockedPerformancesByInviteCode
    },

    isPerformanceUnlockedByInviteCode(performance: PerformanceListRead): boolean {
      return this.unlockedPerformancesByInviteCode.some(p => p.id === performance.id)
    },

    async refreshPerformance() {
      if (!this.performance?.id) throw new Error('No performance id is set')
      const response = await api.getPerformance(
        this.performance.id,
        this.invite_code ? { invite_code: this.invite_code } : undefined,
      )
      this.performance = parsePerformance(response.data)
    },

    async setPerformance(performance: StorePerformance, error: ErrorData) {
      clearError(error)
      try {
        if (performance.sold) {
          error.code = 'PERFORMANCE_SOLD_OUT'
          error.message = 'That performance is sold out.'
          return false
        }
        const settingStore = useSettingStore()
        if (!this.isPerformanceVisibleOnTheList(performance)) {
          error.code = 'PAST_PERFORMANCE'
          error.message = 'That performance is in the past.'
          return false
        }
        this.performance = performance
        if (settingStore.max_guests == 1) {
          await this.refreshPerformance()
          // for livestreaming venues we don't ask for number of guests, just assume 1
          return await this.setNumberOfGuests(1, error)
        } else {
          // if we are not livestreaming, we ask for number of guests every time
          this.clearNumberOfGuests()
          await this.$router.push(this.nextRoute)
          return true
        }
      } catch (newError) {
        console.error(newError)
        Sentry.captureException(newError)
        error.code = 'UNKNOWN'
        error.message = 'An error occured'
        return false
      }
    },
    async setNumberOfGuests(numberOfGuests: number, error: ErrorData) {
      clearError(error)
      this.number_of_guests = numberOfGuests
      if (this.standingRoomOnly) {
        // if there is only one table and it is standing room only, we skip table selection
        return await this.setTables([this.performance!.tables![0].id], error)
      } else {
        this.clearTables()
        await this.$router.push(this.nextRoute)
        return true
      }
    },
    async setTables(tables: PerformanceTableId[], error: ErrorData) {
      clearError(error)
      this.tables = tables
      try {
        await this.syncReservation()
      } catch (e) {
        this.performance = await this.getPerformance(this.performance!.id)
        this.clearTables()
        throw e
      }
      if (
        useSettingStore().flagEnabled(FeatureFlag.SKIP_SEATING_TIME) &&
        this.performance?.seating_times?.length === 1
      ) {
        return await this.setSeatingTime(this.performance.seating_times[0], error)
      } else {
        this.clearSeatingTime()
        await this.$router.push(this.nextRoute)
        return true
      }
    },

    async setSeatingTime(seatingTime: StorePerformanceSeatingTime, error: ErrorData) {
      clearError(error)
      if (seatingTime.sold) {
        error.code = 'SEATING_TIME_SOLD_OUT'
        error.message = 'That seating time is sold out.'
        return false
      }
      this.seating_time = seatingTime
      this.clearAddOns()
      await this.syncReservation()
      await this.$router.push(this.nextRoute)
      return true
    },

    async setAddOns(addOns: any[], error: ErrorData) {
      clearError(error)
      this.add_ons = addOns
      await this.syncReservation()

      await this.$router.push(this.nextRoute)
      return true
    },

    async setContactInfo() {
      await this.updateGuestInfo()
      if (this.hasSufficientGuestInfo) {
        if (this.requiresPayment) await this.$router.push({ name: 'payment' })
        else await this.checkout()
      }
    },

    setUser(user: StoreUser) {
      this.contact.first_name = user.first_name || this.contact.first_name
      this.contact.last_name = user.last_name || this.contact.last_name
      this.contact.email = user.email || this.contact.email
      this.contact.phone = user.phone || this.contact.phone
    },
    async setPromoCode(promo: StorePromotionCode | null) {
      try {
        if (!promo) this.promo_code = null
        this.promo_code = promo
        await this.syncReservation()
        this.hasPromoCodeApplied = true
      } catch (e) {
        this.promo_code = null
        this.hasPromoCodeApplied = false
        throw e
      }
    },
    setInviteCode(code: string | undefined) {
      this.invite_code = code || null
    },

    async validatePromotionCode(code: string): Promise<PromotionCodeRead> {
      if (!this.performance) {
        throw new Error('No performance is set. Cannot validate promotion code.')
      } else return (await api.validatePromotionCode(code, this.performance.id)).data
    },
    setScrollPositionFromSetShow(position: number | null) {
      this.scroll_position_from_set_show = position
    },

    handleStripeResponse(stripeResp: stripe.PaymentIntentResult | undefined) {
      if (!stripeResp) {
        throw mkHandledError('stripe_error', 'There was an error processing your payment.')
      } else if (stripeResp.error) {
        throw enhanceStripeError(stripeResp.error)
      }
    },

    // Ensure that the client secret is either null or a non-empty string
    setClientSecret(secret: string | null | undefined) {
      this.client_secret = secret || null
    },

    handleClientSecretFromResponse(response: AxiosResponse<ReservationDetailedRead>) {
      if (response.data.transactions?.length > 0 && response.data.transactions[0].client_secret) {
        this.setClientSecret(response.data.transactions[0].client_secret)
      }
    },

    async syncReservation() {
      if (!this.performance) throw new Error('No performance is set')
      if (!this.reservation || this.reservation.performance_id !== this.performance.id) {
        const response = await api.reserveReservation({
          ...this.reservationGuestData,
          performance: this.performance.id,
        })
        this.reservation_key = response.data.reservation_key
        this.reservation = response.data.reservation
        this.setClientSecret(response.data.client_secret)
      } else {
        try {
          const response = await api.guestModifyReservation(
            this.reservation.id,
            this.reservationGuestData,
          )
          this.reservation = response.data
          this.handleClientSecretFromResponse(response)
        } catch (e) {
          this.clearAll()
          this.$router.push({ name: 'setShow' })
        }
      }
      if (!useUserStore().isLoggedIn && this.reservation?.contact) {
        try {
          window.hj('identify', `g-${this.reservation.contact.id}`, {
            email: this.contact.email,
          })
        } catch {
          // shrug
        }
      }
    },

    async retryReservation() {
      if (!this.performance) throw new Error('No performance is set')
      if (!this.reservation) throw new Error('No reservation is set')
      await api.retryReservation(this.reservation.id)
      await this.syncReservation()
    },

    async updateGuestInfo() {
      if (!this.guestInfoData || !this.reservation) return
      const response = await api.guestInfoReservation(this.reservation.id, {
        ...this.guestInfoData,
        reservation_key: this.reservation_key,
      })
      this.reservation = response.data
    },

    async checkout(stripeResponse: stripe.PaymentIntentResult | undefined) {
      if (stripeResponse) {
        this.handleStripeResponse(stripeResponse)
      }
      await this.onSuccessfulPurchase()
    },

    async checkoutWithGiftCard(
      gift_card_number: string,
      gift_card_pin: string,
    ): Promise<void | boolean> {
      if (!this.reservation) return
      const { data } = await ReservationService.payWithGiftCard({
        gift_card_number,
        gift_card_pin,
        reservationId: this.reservation.id,
        reservation_key: this.reservation_key as string,
      })
      this.gift_card = {
        number: gift_card_number,
        balance: this.pricing.total - data.reservation_balance,
      }
      this.client_secret = data.client_secret
      this.reservation_key = data.reservation_key
      if (data.reservation_balance > 0.5) return false
      else await this.onSuccessfulPurchase()
    },

    async onSuccessfulPurchase() {
      this.checkout_success = true

      if (!this.performance) return
      const posthog = usePostHog()
      const settingStore = useSettingStore()
      posthog.capture('Order Completed FE', {
        performance_id: this.performance.id,
        show_name: this.performance.show.name,
        application_fee: this.pricing.turntableFee,
        number_of_guests: this.number_of_guests,
        amount: this.pricing.amount,
        add_on_amount: this.pricing.addOnAmount,
        tax: this.pricing.tax,
        service_fee: this.pricing.serviceFee,
        discount: this.pricing.discounted,
        subtotal: this.pricing.amount + this.pricing.addOnAmount,
        total: this.pricing.total,
        email: this.contact.email,
        venue: settingStore.venue_slug,
        $insert_id: this.reservation?.id,
      })

      if (window.gtag) {
        window.gtag('event', 'purchase', {
          transaction_id: this.reservation?.id,
          value: this.pricing.total,
          currency: settingStore.venue_currency,
          reservation_id: this.reservation?.id,
          items: [
            {
              item_id: this.performance.id,
              item_name: this.performance.show.name,
              item_variant: this.tableModels.map(t => t.name).join(', '),
              price: this.pricing.total,
              number_of_guests: this.number_of_guests,
            },
          ],
        })
      }

      this.captureFacebookPurchaseEvent()

      await this.$router.push({
        name: 'order-confirmation',
        params: {
          reservationId: this.reservation!.id,
          reservationKey: this.reservation_key,
        },
      })
    },

    captureFacebookPurchaseEvent() {
      const settingStore = useSettingStore()
      const value = Number(this.pricing.total)
      const currency = settingStore.venue_currency.toUpperCase() || 'USD'
      const content_name = this.performance?.show.name
      const content_ids = [this.performance?.id]
      const contents = this.tableModels.map(t => ({ id: t.id, quantity: t.guests }))
      const num_items = this.number_of_guests
      const order_id = this.reservation?.id

      if (
        window.fbq &&
        value >= 0 &&
        currency &&
        content_name &&
        content_ids.length > 0 &&
        num_items &&
        order_id
      ) {
        window.fbq(
          'track',
          'Purchase',
          {
            value,
            currency,
            content_name,
            content_ids,
            contents,
            num_items,
            order_id,
          },
          { eventID: order_id },
        )
      }
    },

    setIsDesktopFlow(isDesktopFlow: boolean) {
      this.is_desktop_flow = isDesktopFlow
    },

    async abandonAnonymousReservation() {
      if (this.checkout_success || !this.reservation || !this.reservation_key) return
      await api.abandonReservation(this.reservation.id, { reservation_key: this.reservation_key })
      this.reservation = null
      this.reservation_key = null
      this.client_secret = null
    },
  },
})
